import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as airlinesApi from './airlines.api';

import type { AirlineListQuery, CreateAirlineRequest, UpdateAirlineRequest } from '@utazzo/shared';

const QK = {
  list: (query: AirlineListQuery) => ['airlines', 'list', query] as const,
  detail: (id: string) => ['airlines', 'detail', id] as const,
  countryOptions: ['countries', 'options'] as const,
};

export const useAirlinesList = (query: AirlineListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => airlinesApi.listAirlines(query),
    staleTime: 30_000,
  });

export const useCountryOptions = () =>
  useQuery({
    queryKey: QK.countryOptions,
    queryFn: () => airlinesApi.listCountryOptions(),
    staleTime: 5 * 60_000,
  });

export const useCreateAirline = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAirlineRequest) => airlinesApi.createAirline(payload),
    onSuccess: (airline) => {
      qc.invalidateQueries({ queryKey: ['airlines', 'list'] });
      toast.success(`Added ${airline.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add airline'),
  });
};

export const useUpdateAirline = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAirlineRequest }) =>
      airlinesApi.updateAirline(id, payload),
    onSuccess: (airline) => {
      qc.invalidateQueries({ queryKey: ['airlines', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(airline.id) });
      toast.success('Airline updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update airline'),
  });
};

export const useDeleteAirline = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => airlinesApi.deleteAirline(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['airlines', 'list'] });
      toast.success('Airline deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete airline'),
  });
};

export const useBulkDeleteAirlines = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => airlinesApi.bulkDeleteAirlines(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['airlines', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete airlines'),
  });
};

export const useBulkSetAirlinesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      airlinesApi.bulkSetAirlinesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['airlines', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update airline status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
